import axios from "axios";
import utils from "./utils";

/**
 * Class for making authenticated HTTP requests using tenant-based endpoints.
 */
class Request {

  /**
   * Creates an instance of the `Request` class.
   *
   * @param {Authenticate} auth - An authentication object for managing tokens and login state.
   * @param {Tenants} tenants - An object managing tenant-related data.
   */
  constructor(auth, tenants) {
    /**
     * @property {Authenticate} auth - The authentication instance.
     */
    this.auth = auth;

    /**
     * @property {Tenants} tenants - The tenants instance for accessing tenant data.
     */
    this.tenants = tenants;
  }

  /**
   * Makes an HTTP request to a tenant's API endpoint.
   *
   * @param {string} servicePath - The service endpoint path relative to the base API URL.
   * @param {Object} [options={}] - The request options, including method, headers, and data.
   * @param {string} [options.method='GET'] - The HTTP method (e.g., GET, POST, PUT).
   * @param {Object} [options.data=null] - The data to be sent in the request body (for POST, PUT, etc.).
   * @returns {Promise<*>} Resolves with the response data if the request is successful.
   * @throws {Error} Throws an error for failed requests or unexpected response statuses.
   */
  async request(servicePath, options = {}) {
    utils.log('Request', servicePath, options);

    if (!this.auth.isAuthenticated()) {
      return null;
    }

    if (!options.method) {
      options.method = 'GET';
    }

    try {
      const url = this.tenants.getCurrent().endpoint + '/api/v1/' + servicePath;
      const config = {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + this.tenants.getCurrent().accessToken
        }
      }
      const data = options.data || null;

      const m = options.method.toLowerCase();

      const response = ['POST', 'PUT', 'PATCH'].includes(options.method) ?
        await axios[m](url, data, config) :
        await axios[m](url, config);

      // Check if the response was successful
      if (response.status === 200) {
        utils.log('Request response', response.data);
        return response.data.data;

      } else {
        utils.errorMessage('Request failed.');
        utils.logError(response.data);
        throw new Error(`Unexpected status code: ${response.status}`);
      }

    } catch (error) {
      utils.logError('Request error', error);

      if (error.response) {
        // Server responded with a status other than 200 range
        const errorData = error.response.data;

        if (!options.isRefresh && errorData.error === 'token_expired') {
          return await this.refreshRequest(servicePath, options);
        }
      }

      throw new Error(error);
    }
  }

  /**
   * Refreshes tokens and retries a failed request.
   *
   * @param {string} servicePath - The service endpoint path relative to the base API URL.
   * @param {Object} options - The original request options.
   * @returns {Promise<*>} Resolves with the response data if the retried request is successful.
   * @throws {Error} Throws an error if the retry fails.
   */
  async refreshRequest(servicePath, options) {
      await this.auth.refreshTokens();

      return await this.request(servicePath, {
        ...options,
        'isRefresh': true
      });
  }

  /**
   * Uploads a file to an existing health and safety report.
   *
   * @param {string} servicePath - The service endpoint path relative to the base API URL.
   * @param {File} file - The updated file for the report.
   * @returns {Promise<*>} Resolves with the response data if the request is successful.
   * @throws {Error} Throws an error for failed requests or unexpected response statuses.
   */
  async uploadFile(servicePath, file) {
    utils.log('Request', servicePath, file);

    if (!this.auth.isAuthenticated()) {
      return null;
    }

    const url = this.tenants.getCurrent().endpoint + '/api/v1/' + servicePath;
    const config = {
      headers: {
        'Accept': 'application/json',
        'Authorization': 'Bearer ' + this.tenants.getCurrent().accessToken
      }
    }

    // Create a FormData object to hold the file
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Make the POST request with fetch
      const response = await fetch(url, {
        method: 'POST',
        ...config,
        body: formData
      });

      // Handle the response
      if (response.ok) {
        const data = await response.json();
        console.log('File uploaded successfully:', data);
      } else {
        const error = await response.json();
        console.error('Error uploading file:', error);
      }
    } catch (err) {
      console.error('An unexpected error occurred:', err);
    }
  }

}

export default Request;